import {fluxDarkThemeColors} from "@buildwithflux/models/fluxDarkThemeColors";
import {grey} from "@mui/material/colors";
import Grow from "@mui/material/Grow";
import type {Theme, ThemeOptions} from "@mui/material/styles";
import Zoom from "@mui/material/Zoom";

import fonts from "./fonts";
import menuClasses from "./menuClasses";

// This is needed to add custom keys to the theme for typescript.
// See https://material-ui.com/customization/palette/#adding-new-colors
declare module "@mui/material/styles" {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypeBackground {
        canvas?: string;
        snackbar?: string;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypeText {
        hint?: string;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface PaletteColor {
        darkText?: string;
        lightBg?: string;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface SimplePaletteColorOptions {
        darkText?: string;
        lightBg?: string;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Color {
        950?: string;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Palette {
        other: {stroke: string};
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface PaletteOptions {
        other?: {stroke: string};
    }
}

const isWebKit154 =
    typeof navigator !== "undefined" &&
    /^((?!chrome|android).)*(safari|mobile)/i.test(navigator.userAgent) &&
    /(os |version\/)15(.|_)[4-9]/i.test(navigator.userAgent);

const tempTrans = {
    slots: {
        transition: isWebKit154 ? Zoom : Grow,
    },
};

export const paperBackgroundColor = fluxDarkThemeColors.backgroundPaper;

const legacyBreakpointValues = {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
};

const getMenuIconClassStyleOverrides = () => ({
    [`&.${menuClasses.icon}`]: {fontSize: "18px"},
    [`&.${menuClasses.rightArrow}`]: {verticalAlign: "middle"},
    [`&.${menuClasses.icons90Deg}`]: {transform: "rotate(90deg)"},
    [`&.${menuClasses.icons270Deg}`]: {transform: "rotate(270deg)"},
});

const getMenuItemClassStyleOverrides = (theme: Theme) => ({
    [`&.${menuClasses.menuItem}`]: {width: theme.spacing(40)},
    [`&.${menuClasses.menuItemWithShortcut}`]: {
        position: "relative",
        paddingRight: theme.spacing(15),
    },
    [`& .${menuClasses.rightLabel}`]: {color: theme.palette.text.secondary},
    [`& .${menuClasses.rightLabelSpacer}`]: {visibility: "hidden"},
    [`&.${menuClasses.containsChip}`]: {paddingRight: "100px"},
});

// NOTE: Light mode has never been tested. It is here for future compatibility.
export function getThemeConfig(prefersDarkMode: boolean): ThemeOptions {
    if (prefersDarkMode) {
        return {
            breakpoints: {
                values: legacyBreakpointValues,
            },
            // see https://material-ui.com/customization/palette/
            palette: {
                mode: "dark",
                // see https://www.figma.com/file/ddlwY6dkQdcNhVTEc7R7GV/%E2%9D%87%EF%B8%8F-Flux-Design-System?node-id=0%3A7243
                text: {
                    disabled: "rgba(255, 255, 255, 0.38)",
                    hint: "rgba(255, 255, 255, 0.38)",
                    primary: fluxDarkThemeColors.textPrimary,
                    secondary: fluxDarkThemeColors.textSecondary,
                },
                grey: {
                    // `grey-950` — one step darker than MUI grey[900] (#212121);
                    // used by the big-chat (`ab-big-chat`) dark surfaces.
                    950: "#181818",
                },

                /*
                 * Looking for 'background', 'lightbg' or 'border' colors from Figma? Use the following:
                 *
                 * - The background color is the main color set to 8% (0.08) opacity.
                 * - The border color is the main color set to 50% (0.5) opacity.
                 * - The lightbg color is the main color darkened by 90%
                 *
                 * ```ts
                 * import {alpha, darken} from "@mui/material/styles";
                 *
                 * const primaryBorderWithBackgroundSx = (theme: Theme) => ({
                 *     backgroundColor: alpha(theme.palette.primary.main, 0.08),
                 *     borderColor: alpha(theme.palette.primary.main, 0.5),
                 * });
                 *
                 * const warningAlertSx = (theme: Theme) => ({
                 *     backgroundColor: darken(theme.palette.warning.main, 0.9),
                 * });
                 * ```
                 *
                 * Here, `alpha(color, amount)` means to set the absolute opacity of the color to the amount (it could have
                 * been called setOpacity(color, alpha))
                 */

                primary: {
                    contrastText: "#000000",
                    dark: "#00C990",
                    light: "#9DFEDA",
                    main: fluxDarkThemeColors.primaryMain,
                },

                secondary: {
                    contrastText: "#000000",
                    dark: "#0083CB",
                    light: "#69E4FF",
                    main: fluxDarkThemeColors.secondaryMain,
                },

                success: {
                    contrastText: "#FFFFFF",
                    dark: "#3B873E",
                    darkText: "rgba(183, 223, 185, 1)",
                    light: "#7BC67E",
                    lightBg: "rgba(8, 18, 8, 1)",
                    main: fluxDarkThemeColors.successMain,
                },

                info: {
                    contrastText: "#FFFFFF",
                    dark: "#0B79D0",
                    darkText: "#A6D5FA",
                    light: "#64B6F7",
                    lightBg: "rgba(3, 15, 24, 1)",
                    main: fluxDarkThemeColors.infoMain,
                },

                warning: {
                    contrastText: "rgba(0, 0, 0, 0.87)",
                    dark: "#C77700",
                    light: "#FFB547",
                    main: fluxDarkThemeColors.warningMain,
                },

                error: {
                    contrastText: "#FFFFFF",
                    dark: "#E31B0C",
                    darkText: "#FAB3AE",
                    light: "#F88078",
                    lightBg: "rgba(24, 7, 5, 1)",
                    main: fluxDarkThemeColors.errorMain,
                },

                background: {
                    paper: paperBackgroundColor,
                    canvas: "#100F0F",
                    snackbar: grey["800"],
                    // QUESTION: "default" is previous to design system update... do we still need it?
                    // index.html also uses a hard-coded value for background-color. Keep these in sync.
                    // This is not the same as the canvas background color; for that, see colors.ts:colors.canvas.background
                    default: fluxDarkThemeColors.backgroundDefault,
                },
                divider: fluxDarkThemeColors.divider,
                other: {
                    stroke: "rgba(255, 255, 255, 0.38)",
                },
            },
            typography: {
                fontFamily: [fonts.primary, "sans-serif"].join(","),
            },
            components: {
                // fix safari flicker issue as per https://github.com/mui/material-ui/pull/32304
                MuiPopover: {
                    defaultProps: tempTrans,
                },
                MuiTooltip: {
                    defaultProps: {
                        ...tempTrans,
                        enterDelay: 1000,
                        enterNextDelay: 100,
                        // Tooltips should not capture pointer events (hovering over a
                        // tooltip keeps it open and blocks elements underneath it).
                        // Individual tooltips can opt back in with disableInteractive={false}.
                        disableInteractive: true,
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundImage: "none",
                        },
                    },
                },
                MuiDivider: {
                    styleOverrides: {
                        root: {
                            height: 1,
                            border: 0,
                            backgroundColor: fluxDarkThemeColors.divider,
                        },
                        vertical: {
                            width: 1,
                            height: "auto",
                        },
                    },
                },
                MuiGrid: {
                    styleOverrides: {
                        root: ({ownerState, theme}) => {
                            // Preserve the established negative half-margins and
                            // child half-padding instead of modern CSS gap spacing.
                            if (
                                !ownerState.container ||
                                typeof ownerState.spacing !== "number" ||
                                ownerState.spacing === 0
                            ) {
                                return {};
                            }

                            const spacing = theme.spacing(ownerState.spacing);
                            const halfSpacing = theme.spacing(ownerState.spacing / 2);

                            return {
                                "--Grid-rowSpacing": "0px",
                                "--Grid-columnSpacing": "0px",
                                width: `calc(100% + ${spacing})`,
                                margin: `-${halfSpacing}`,
                                gap: "normal",
                                "& > .MuiGrid-root": {
                                    // Prevent MUI's gap-aware item sizing from subtracting
                                    // spacing that the legacy child padding already provides.
                                    "--Grid-parent-rowSpacing": "0px",
                                    "--Grid-parent-columnSpacing": "0px",
                                    padding: halfSpacing,
                                },
                            };
                        },
                    },
                },
                MuiList: {
                    styleOverrides: {
                        root: {
                            // MUI 9 expands menu dividers to 8px above and below.
                            // Apply the legacy 4px rhythm to both Menu popovers and
                            // standalone MenuList surfaces such as the PCB layer menu.
                            '&&[role="menu"] > .MuiDivider-root': {
                                margin: "4px 0",
                            },
                        },
                    },
                },
                MuiMenu: {
                    styleOverrides: {
                        paper: {
                            backgroundColor: grey["800"],
                        },
                        list: {
                            // Keep every section on the menu paper surface. MUI 9
                            // otherwise gives ListSubheader its own background.paper
                            // color, producing alternating bands within one menu.
                            backgroundColor: "inherit",
                            "& .MuiListSubheader-root": {
                                backgroundColor: "inherit",
                            },
                            // MenuItem's nested icon rule otherwise overrides the
                            // legacy MuiListItemIcon width below (36px vs 29px).
                            "&& .MuiListItemIcon-root": {
                                minWidth: "29px",
                            },
                            "& .MuiListItemSecondaryAction-root": {
                                marginLeft: "18px",
                            },
                            "& .MuiMenuItem-root": {
                                paddingRight: "100px",
                            },
                            "& .MuiListSubheader-root.dense": {
                                padding: "7px 16px",
                                lineHeight: "initial",
                            },
                        },
                    },
                },
                MuiMenuItem: {
                    styleOverrides: {
                        root: ({theme}) => ({
                            ...getMenuItemClassStyleOverrides(theme),
                            // MUI 4 used the neutral action color for selected list
                            // items. Newer MUI versions tint selected MenuItems with
                            // the primary color, which is too prominent in our dark
                            // menus and makes selection look like an active control.
                            "&.Mui-selected, &.Mui-selected:hover": {
                                backgroundColor: theme.palette.action.selected,
                            },
                        }),
                    },
                },
                MuiListItemIcon: {
                    styleOverrides: {
                        root: {minWidth: "29px"},
                    },
                },
                MuiDialog: {
                    styleOverrides: {
                        root: {
                            "& .MuiDialogContent-dividers": {
                                padding: "0",
                            },
                        },
                    },
                },
                MuiAutocomplete: {
                    styleOverrides: {
                        paper: {
                            backgroundColor: grey["800"],
                        },
                        listbox: ({theme}) => ({
                            "& .MuiAutocomplete-option.Mui-focused": {
                                backgroundColor: theme.palette.action.hover,
                            },
                            '& .MuiAutocomplete-option[aria-selected="true"]': {
                                backgroundColor: theme.palette.action.selected,
                            },
                            '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                                backgroundColor: theme.palette.action.selected,
                            },
                            "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
                                backgroundColor: grey["800"],
                            },

                            "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                                backgroundColor: grey["600"],
                                border: "3px solid " + grey["800"],
                            },
                        }),
                    },
                },
                MuiButton: {
                    defaultProps: {
                        color: "inherit",
                    },
                    styleOverrides: {
                        root: {
                            textTransform: "none",
                            fontSize: "15px", // Default size (medium) gets 15px
                        },
                        sizeSmall: {
                            fontSize: "14px", // Small buttons get 14px
                        },
                    },
                    variants: [
                        {
                            props: {color: "inherit", variant: "contained"},
                            style: {
                                color: "rgba(0, 0, 0, 0.87)",
                                backgroundColor: grey["300"],
                                "&:hover": {
                                    backgroundColor: grey["A100"],
                                    "@media (hover: none)": {
                                        backgroundColor: grey["300"],
                                    },
                                },
                            },
                        },
                    ],
                },
                MuiChip: {
                    styleOverrides: {
                        root: {
                            lineHeight: 1.43,
                            "&.MuiChip-sizeSmall .MuiChip-label": {
                                paddingLeft: 8,
                                paddingRight: 8,
                            },
                        },
                    },
                    variants: [
                        {
                            props: {color: "default", variant: "outlined"},
                            style: {
                                borderColor: "rgba(255, 255, 255, 0.23)",
                            },
                        },
                    ],
                },
                MuiIconButton: {
                    styleOverrides: {
                        sizeSmall: {
                            padding: "3px",
                            fontSize: "1.125rem",
                        },
                    },
                },
                MuiLink: {
                    defaultProps: {
                        underline: "hover",
                    },
                },
                MuiTab: {
                    styleOverrides: {
                        root: {
                            textTransform: "none",
                            fontSize: "15px",
                            lineHeight: 1.75,
                            padding: "6px 12px",
                        },
                    },
                },
                MuiFab: {
                    styleOverrides: {
                        root: {
                            textTransform: "none",
                        },
                    },
                },
                MuiCssBaseline: {
                    styleOverrides: (theme) => ({
                        // Apply the custom dark scrollbar to BOTH html and body.
                        // The viewport scrollbar is attached to `html` on most app
                        // pages (profile, project list, etc) — styling only `body`
                        // left the root scrollbar as Chrome's default light
                        // scrollbar, which did not match the dark app chrome. By
                        // styling `html` we ensure every page in the app AND the
                        // docs-site render the same dark custom scrollbar.
                        "html, body": {
                            scrollbarColor: grey["600"] + " transparent" /* For Firefox */,
                            "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
                                backgroundColor: paperBackgroundColor,
                                width: "8px",
                                height: "8px",
                            },
                            "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                                borderRadius: 8,
                                backgroundColor: grey["600"],
                                minHeight: 24,
                                border: "3px solid transparent",
                            },
                            "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
                                backgroundColor: grey["500"],
                            },
                            "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active": {
                                backgroundColor: grey["500"],
                            },
                            "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
                                backgroundColor: grey["600"],
                            },
                            "&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
                                backgroundColor: "transparent",
                            },
                        },
                        // body-only rules: hide the native Firefox scrollbar when
                        // body itself is the scrolling element (the webkit rules
                        // above still render the custom track/thumb in Chromium);
                        // and reset the search-input cancel button.
                        body: {
                            fontFamily: [fonts.primary, "sans-serif"].join(","),
                            fontSize: "0.875rem",
                            fontWeight: 400,
                            lineHeight: 1.43,
                            letterSpacing: "normal",
                            scrollbarWidth: "none" /* For Firefox */,
                            // See https://stackoverflow.com/a/64267916/200312
                            '& input[type="search"]::-webkit-search-cancel-button': {
                                WebkitAppearance: "none",
                            },
                        },
                    }),
                },
                MuiSvgIcon: {
                    styleOverrides: {
                        root: getMenuIconClassStyleOverrides,
                    },
                },
            },
            zIndex: {
                modal: 10000,
                // MUI's default snackbar (1400) and tooltip (1500) are below our
                // modal z-index, so scale them up to preserve the default
                // modal < snackbar < tooltip ordering and keep notistack/Backdrop
                // above dialog backdrops.
                snackbar: 10001,
                tooltip: 10002,
            },
        };
    } else {
        return {
            breakpoints: {
                values: legacyBreakpointValues,
            },
            palette: {
                mode: "light",
                grey: {
                    // `grey-950` — one step darker than MUI grey[900] (#212121);
                    // used by the big-chat (`ab-big-chat`) dark surfaces.
                    950: "#181818",
                },
            },
            typography: {
                fontFamily: [fonts.primary, "sans-serif"].join(","),
            },
            components: {
                MuiMenuItem: {
                    styleOverrides: {
                        root: ({theme}) => getMenuItemClassStyleOverrides(theme),
                    },
                },
                MuiSvgIcon: {
                    styleOverrides: {
                        root: getMenuIconClassStyleOverrides,
                    },
                },
            },
        };
    }
}
